/**
 * `ItineraryCard` — the distinct browse card for a public itinerary (U8).
 *
 * House style mirrors `VenueCard`/`EventCard` (warm paper surface, primary-*
 * brand tokens, Young-Serif editorial title via the `Typography` `venueName`
 * serif variant). Itineraries carry no cover asset in Phase 1+2, so the 16:9
 * media band is a deliberate brand "no-image" treatment (not a broken image)
 * — this is the graceful fallback the IA contract asks for.
 *
 * - `averageRating` renders ONLY when non-null (absent — never "0" — when the
 *   itinerary has no reviews; the server already nulls it).
 * - Byline chip: an "Editors' Pick" treatment for `kind: 'editorial'`
 *   (curated `ownerType='wineryxplorer'`), otherwise the community handle.
 * - Routes by slug only (D5) → `/itineraries/p/:slug` (the PUBLIC view route).
 * - A dangling-venue stop never breaks the card: it only renders day/stop
 *   counts the server computed (D4).
 */

import { Link } from 'react-router-dom';
import { Typography } from '../ui/Typography';

export interface ItineraryCardData {
  slug: string;
  title: string;
  regionNames: string[];
  dayCount: number;
  stopCount: number;
  averageRating: number | null;
  reviewCount: number;
  byline: {
    kind: 'editorial' | 'community';
    label: string;
  };
}

interface ItineraryCardProps {
  itinerary: ItineraryCardData;
}

const pluralize = (n: number, word: string) =>
  `${n} ${word}${n === 1 ? '' : 's'}`;

export const ItineraryCard = ({ itinerary: it }: ItineraryCardProps) => {
  const isEditorial = it.byline.kind === 'editorial';
  const regionLabel =
    it.regionNames.length > 0 ? it.regionNames.join(' · ') : 'Multi-region';

  return (
    <Link
      to={`/itineraries/p/${it.slug}`}
      className="group flex flex-col bg-gray-100 border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow touch-manipulation"
    >
      {/* 16:9 media band — brand no-image treatment (itineraries have no
          cover asset in Phase 1+2). */}
      <div className="relative aspect-[16/9] w-full bg-primary-50 border-b border-gray-200">
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            aria-hidden="true"
            className="font-serif text-5xl text-primary-200 select-none"
          >
            ✦
          </span>
        </div>
        {isEditorial && (
          <span className="absolute top-2 left-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-800 border border-primary-200">
            Editors&rsquo; Pick
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 p-3">
        <Typography
          variant="venueName"
          as="h3"
          className="text-gray-900 group-hover:text-primary-700 leading-tight text-xl sm:text-2xl"
        >
          {it.title}
        </Typography>

        {/* Region + day + stop meta (quiet sans metadata). */}
        <Typography
          variant="bodySmall"
          as="p"
          className="text-gray-600"
        >
          {regionLabel} &middot; {pluralize(it.dayCount, 'day')} &middot;{' '}
          {pluralize(it.stopCount, 'stop')}
        </Typography>

        <div className="flex items-center justify-between mt-1">
          {/* Byline chip. */}
          <span
            className={
              isEditorial
                ? 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-800 border border-primary-200'
                : 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200'
            }
          >
            {isEditorial ? "Editors' Pick" : it.byline.label}
          </span>

          {/* Rating — present ONLY when the server returned a non-null
              averageRating (i.e. there is at least one review). */}
          {it.averageRating !== null && (
            <span className="inline-flex items-center gap-1 text-sm text-gray-700">
              <span aria-hidden="true" className="text-primary-700">
                ★
              </span>
              <span className="font-medium">
                {it.averageRating.toFixed(1)}
              </span>
              <span className="text-gray-500">
                ({it.reviewCount})
              </span>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
